import React, { useState, useEffect } from 'react';
import { ShoppingCart, Star, TrendingUp, DollarSign, Filter, Search, Crown, Users, Eye, Heart } from 'lucide-react';
import { database } from '../../Config/firebase';
import { ref, onValue, push, set, update } from '../../Config/matuRtdb';

const PredictionMarketplace = () => {
  const [predictions, setPredictions] = useState([]);
  const [userPredictions, setUserPredictions] = useState([]);
  const [selectedPrediction, setSelectedPrediction] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [filters, setFilters] = useState({
    sport: 'all',
    priceRange: 'all',
    rating: 'all',
    sellerType: 'all'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  // Predicciones de ejemplo
  const samplePredictions = [
    {
      id: 'pred1',
      title: 'Real Madrid vs Barcelona - Análisis Completo',
      description: 'Predicción basada en estadísticas avanzadas y análisis de formaciones',
      sport: 'football',
      league: 'La Liga',
      match: 'Real Madrid vs Barcelona',
      prediction: 'Real Madrid gana',
      confidence: 85,
      price: 5000,
      seller: {
        id: 'seller1',
        name: 'ProAnalyst',
        rating: 4.8,
        totalSales: 150,
        isVIP: true,
        avatar: 'https://avatar.iran.liara.run/public/boy?username=ProAnalyst'
      },
      stats: {
        accuracy: 78,
        totalPredictions: 200,
        wins: 156
      },
      tags: ['La Liga', 'Clásico', 'Alta Confianza'],
      createdAt: Date.now() - 3600000,
      purchases: 23,
      rating: 4.9,
      reviews: 18
    },
    {
      id: 'pred2',
      title: 'Manchester City - Triple Análisis',
      description: 'Análisis completo de los próximos 3 partidos de City',
      sport: 'football',
      league: 'Premier League',
      match: 'Manchester City vs Liverpool',
      prediction: 'Over 2.5 goles',
      confidence: 72,
      price: 3000,
      seller: {
        id: 'seller2',
        name: 'PremierExpert',
        rating: 4.6,
        totalSales: 89,
        isVIP: false,
        avatar: 'https://avatar.iran.liara.run/public/boy?username=PremierExpert'
      },
      stats: {
        accuracy: 71,
        totalPredictions: 120,
        wins: 85
      },
      tags: ['Premier League', 'Over/Under', 'Múltiples Partidos'],
      createdAt: Date.now() - 7200000,
      purchases: 15,
      rating: 4.7,
      reviews: 12
    },
    {
      id: 'pred3',
      title: 'NBA Playoffs - Series Completa',
      description: 'Predicción para toda la serie de playoffs Lakers vs Warriors',
      sport: 'basketball',
      league: 'NBA',
      match: 'Lakers vs Warriors',
      prediction: 'Lakers en 6 partidos',
      confidence: 68,
      price: 8000,
      seller: {
        id: 'seller3',
        name: 'NBAMaster',
        rating: 4.9,
        totalSales: 234,
        isVIP: true,
        avatar: 'https://avatar.iran.liara.run/public/boy?username=NBAMaster'
      },
      stats: {
        accuracy: 82,
        totalPredictions: 180,
        wins: 148
      },
      tags: ['NBA', 'Playoffs', 'Serie Completa'],
      createdAt: Date.now() - 10800000,
      purchases: 45,
      rating: 4.8,
      reviews: 32
    }
  ];

  useEffect(() => {
    setPredictions(samplePredictions);
  }, []);

  const filteredPredictions = predictions.filter(prediction => {
    // Filtro por búsqueda
    if (searchTerm && !prediction.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !prediction.description.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }

    // Filtro por deporte
    if (filters.sport !== 'all' && prediction.sport !== filters.sport) {
      return false;
    }

    // Filtro por rango de precio
    if (filters.priceRange !== 'all') {
      const [min, max] = filters.priceRange.split('-').map(Number);
      if (filters.priceRange === '5000+') {
        if (prediction.price < 5000) return false;
      } else if (prediction.price < min || prediction.price > max) {
        return false;
      }
    }

    // Filtro por rating
    if (filters.rating !== 'all') {
      const minRating = Number(filters.rating);
      if (prediction.rating < minRating) return false;
    }

    // Filtro por tipo de vendedor
    if (filters.sellerType !== 'all') {
      if (filters.sellerType === 'vip' && !prediction.seller.isVIP) return false;
      if (filters.sellerType === 'regular' && prediction.seller.isVIP) return false;
    }

    return true;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return b.createdAt - a.createdAt;
      case 'oldest':
        return a.createdAt - b.createdAt;
      case 'price_low':
        return a.price - b.price;
      case 'price_high':
        return b.price - a.price;
      case 'rating':
        return b.rating - a.rating;
      case 'popularity':
        return b.purchases - a.purchases;
      default:
        return b.createdAt - a.createdAt;
    }
  });

  const createPrediction = (predictionData) => {
    const newPrediction = {
      id: `pred${Date.now()}`,
      ...predictionData,
      createdAt: Date.now(),
      purchases: 0,
      rating: 0,
      reviews: 0
    };

    setPredictions(prev => [newPrediction, ...prev]);
    setShowCreateModal(false);
  };

  const purchasePrediction = (prediction) => {
    // Simular compra
    const updatedPrediction = {
      ...prediction,
      purchases: prediction.purchases + 1
    };

    setPredictions(prev => 
      prev.map(p => p.id === prediction.id ? updatedPrediction : p)
    );

    setShowPurchaseModal(false);
  };

  const getSportIcon = (sport) => {
    switch (sport) {
      case 'football': return '⚽';
      case 'basketball': return '🏀';
      case 'tennis': return '🎾';
      case 'baseball': return '⚾';
      default: return '🏆';
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 80) return 'text-green-400';
    if (confidence >= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-900 via-blue-900 to-purple-900 text-white p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <ShoppingCart className="w-8 h-8 text-green-400" />
            <h1 className="text-3xl font-bold bg-gradient-to-r from-green-400 to-blue-400 bg-clip-text text-transparent">
              Marketplace de Predicciones
            </h1>
            <TrendingUp className="w-8 h-8 text-purple-400" />
          </div>
          <p className="text-gray-300 text-lg">
            Compra y vende predicciones de expertos. Gana dinero con el conocimiento
          </p>
        </div>

        {/* Filtros y búsqueda */}
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 mb-8 border border-gray-700/50">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {/* Búsqueda */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Buscar predicciones..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            {/* Filtro por deporte */}
            <select
              value={filters.sport}
              onChange={(e) => setFilters(prev => ({ ...prev, sport: e.target.value }))}
              className="px-4 py-2 bg-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="all">Todos los deportes</option>
              <option value="football">⚽ Fútbol</option>
              <option value="basketball">🏀 Baloncesto</option>
              <option value="tennis">🎾 Tenis</option>
              <option value="baseball">⚾ Béisbol</option>
            </select>

            {/* Filtro por precio */}
            <select
              value={filters.priceRange}
              onChange={(e) => setFilters(prev => ({ ...prev, priceRange: e.target.value }))}
              className="px-4 py-2 bg-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="all">Todos los precios</option>
              <option value="0-1000">$0 - $1,000</option>
              <option value="1000-3000">$1,000 - $3,000</option>
              <option value="3000-5000">$3,000 - $5,000</option>
              <option value="5000+">$5,000+</option>
            </select>

            {/* Ordenar por */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 bg-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="newest">Más recientes</option>
              <option value="oldest">Más antiguos</option>
              <option value="price_low">Precio: Menor a Mayor</option>
              <option value="price_high">Precio: Mayor a Menor</option>
              <option value="rating">Mejor valoradas</option>
              <option value="popularity">Más populares</option>
            </select>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 px-6 py-2 rounded-lg font-medium transition-all flex items-center gap-2"
              >
                <TrendingUp className="w-4 h-4" />
                Vender Predicción
              </button>
            </div>
            <div className="text-sm text-gray-400">
              {filteredPredictions.length} predicciones encontradas
            </div>
          </div>
        </div>

        {/* Grid de predicciones */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPredictions.map((prediction) => (
            <div 
              key={prediction.id}
              className="bg-gray-800/50 backdrop-blur-sm rounded-xl overflow-hidden border border-gray-700/50 hover:border-green-500/50 transition-all cursor-pointer"
              onClick={() => setSelectedPrediction(prediction)}
            >
              {/* Header */}
              <div className="p-4 border-b border-gray-700/50">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{getSportIcon(prediction.sport)}</span>
                    <span className="text-sm text-gray-400">{prediction.league}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 text-yellow-400 fill-current" />
                    <span className="text-sm font-medium">{prediction.rating}</span>
                  </div>
                </div>
                
                <h3 className="font-bold text-lg mb-2">{prediction.title}</h3>
                <p className="text-gray-300 text-sm mb-3">{prediction.description}</p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${getConfidenceColor(prediction.confidence)}`}>
                      {prediction.confidence}% confianza
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-400">{prediction.purchases}</span>
                  </div>
                </div>
              </div>

              {/* Información del vendedor */}
              <div className="p-4 border-b border-gray-700/50">
                <div className="flex items-center gap-3">
                  <img 
                    src={prediction.seller.avatar} 
                    alt={prediction.seller.name}
                    className="w-10 h-10 rounded-full"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{prediction.seller.name}</span>
                      {prediction.seller.isVIP && (
                        <Crown className="w-4 h-4 text-yellow-400" />
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <Star className="w-3 h-3 text-yellow-400 fill-current" />
                      <span>{prediction.seller.rating}</span>
                      <span>•</span>
                      <span>{prediction.seller.totalSales} ventas</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tags y precio */}
              <div className="p-4">
                <div className="flex flex-wrap gap-2 mb-3">
                  {prediction.tags.map((tag, index) => (
                    <span 
                      key={index}
                      className="px-2 py-1 bg-gray-700 text-xs rounded-full text-gray-300"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="text-2xl font-bold text-green-400">
                    ${prediction.price.toLocaleString()}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedPrediction(prediction);
                      setShowPurchaseModal(true);
                    }}
                    className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 px-4 py-2 rounded-lg font-medium transition-all"
                  >
                    Comprar
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Modal de compra */}
        {showPurchaseModal && selectedPrediction && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-2xl p-6 w-full max-w-md mx-4">
              <h3 className="text-xl font-bold mb-4">Comprar Predicción</h3>
              
              <div className="space-y-4 mb-6">
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">{selectedPrediction.title}</h4>
                  <p className="text-sm text-gray-300 mb-3">{selectedPrediction.description}</p>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Partido:</span>
                      <span className="font-medium">{selectedPrediction.match}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Predicción:</span>
                      <span className="font-medium text-green-400">{selectedPrediction.prediction}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Confianza:</span>
                      <span className={`font-medium ${getConfidenceColor(selectedPrediction.confidence)}`}>
                        {selectedPrediction.confidence}%
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span>Precio:</span>
                    <span className="text-2xl font-bold text-green-400">
                      ${selectedPrediction.price.toLocaleString()}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400">
                    Una vez comprada, tendrás acceso inmediato a la predicción completa
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowPurchaseModal(false)}
                  className="flex-1 py-2 px-4 bg-gray-600 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => purchasePrediction(selectedPrediction)}
                  className="flex-1 py-2 px-4 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 rounded-lg transition-colors"
                >
                  Confirmar Compra
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de crear predicción */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-2xl p-6 w-full max-w-lg mx-4">
              <h3 className="text-xl font-bold mb-4">Vender Predicción</h3>
              
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Título de la predicción"
                  className="w-full px-4 py-2 bg-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                
                <textarea
                  placeholder="Descripción detallada de tu análisis"
                  rows="3"
                  className="w-full px-4 py-2 bg-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                />
                
                <div className="grid grid-cols-2 gap-4">
                  <select className="px-4 py-2 bg-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500">
                    <option value="">Seleccionar deporte</option>
                    <option value="football">⚽ Fútbol</option>
                    <option value="basketball">🏀 Baloncesto</option>
                    <option value="tennis">🎾 Tenis</option>
                  </select>
                  
                  <input
                    type="number"
                    placeholder="Precio"
                    className="px-4 py-2 bg-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                
                <input
                  type="number"
                  placeholder="Nivel de confianza (0-100%)"
                  min="0"
                  max="100"
                  className="px-4 py-2 bg-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 py-2 px-4 bg-gray-600 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => createPrediction({
                    title: 'Nueva Predicción',
                    description: 'Descripción de la predicción',
                    sport: 'football',
                    price: 1000,
                    confidence: 75
                  })}
                  className="flex-1 py-2 px-4 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 rounded-lg transition-colors"
                >
                  Publicar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PredictionMarketplace; 